import { type ReactNode, type UIEvent, useEffect, useLayoutEffect, useRef } from "react";

/** Why kept here: a pane is drawn again when the route moves (a list beside the thing opened from it), and it should
 * stay where it was scrolled. */
const kept = new Map<string, number>();

/**
 * The iPad on its side, like Mail: panes side by side under the top bar, each scrolling on its own. Its children are
 * `Pane`s: a list (336 px) at the left, the main pane taking the room, and with `three` a detail pane (356 px) at the right.
 */
export function Panes({ three = false, className, children }: { three?: boolean; className?: string; children: ReactNode }) {
  return <div className={["k-split", three && "k-split-3", className].filter(Boolean).join(" ")}>{children}</div>;
}

const PANE = { list: "k-sp-l", main: "k-sp-m", detail: "k-sp-r" } as const;

/** One pane of `Panes`. The main pane is a section, the list and the detail are asides. */
export function Pane({
  kind,
  keep,
  showing,
  label,
  className,
  children,
}: {
  kind: keyof typeof PANE;
  /** A name to keep the pane's scroll under: drawn again on another route, it opens where it was left. */
  keep?: string;
  /** What the pane shows (an id): when it changes, the pane starts at its top. */
  showing?: string | null;
  label?: string;
  className?: string;
  children: ReactNode;
}) {
  const box = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    if (keep && box.current) box.current.scrollTop = kept.get(keep) ?? 0;
  }, [keep]);
  // Why not on the first draw: a pane drawn again with the same item keeps its place.
  const shown = useRef(showing);
  useEffect(() => {
    if (shown.current === showing) return;
    shown.current = showing;
    if (box.current && showing !== null && showing !== undefined) box.current.scrollTop = 0;
  }, [showing]);
  const props = {
    ref: box,
    className: className ? `${PANE[kind]} ${className}` : PANE[kind],
    "aria-label": label,
    onScroll: keep
      ? (e: UIEvent<HTMLElement>) => {
          kept.set(keep, e.currentTarget.scrollTop);
        }
      : undefined,
  };
  return kind === "main" ? <section {...props}>{children}</section> : <aside {...props}>{children}</aside>;
}
